/**
 * Drive row action buttons — desktop icon strip + overflow ⋮ for secondary actions.
 *
 * Props:
 *   actions     {Array<{ key, label, icon, buttonClass, danger, dataAction, dataParams }>}
 *   menuActions {Array<{ key, label, icon, danger?, onClick }>}  — Preact handlers (rename/move)
 */
import { h } from 'preact'
import { useContext } from 'preact/hooks'
import { DriveI18nContext } from './drive-context.js'

function MenuItem({ action }) {
  if (action.onClick) {
    return (
      <button
        type="button"
        class={`dropdown-item d-flex align-items-center gap-2${action.danger ? ' text-danger' : ''}`}
        onClick={(e) => {
          e.stopPropagation()
          action.onClick(e)
        }}
      >
        <i class={`bi ${action.icon}`} aria-hidden="true"></i>
        <span>{action.label}</span>
      </button>
    )
  }

  return (
    <button
      type="button"
      class={`dropdown-item d-flex align-items-center gap-2${action.danger ? ' text-danger' : ''}`}
      data-action={action.dataAction}
      {...(action.dataParams || {})}
      onClick={(e) => e.stopPropagation()}
    >
      <i class={`bi ${action.icon}`} aria-hidden="true"></i>
      <span>{action.label}</span>
    </button>
  )
}

export function RowActions({ actions = [], menuActions = [] }) {
  const i18n = useContext(DriveI18nContext)
  const t = (key) => (i18n ? i18n.t(key) : key)

  const hasPrimary = actions.length > 0
  const hasMenu = menuActions.length > 0

  if (!hasPrimary && !hasMenu) return null

  const mobileItems = hasMenu && hasPrimary
    ? [...menuActions, { key: '_divider', divider: true }, ...actions]
    : [...menuActions, ...actions]

  return (
    <td class="align-middle text-end drive-row-actions">
      {/* Desktop: icon strip + optional ⋮ for menuActions */}
      <div class="d-none d-md-inline-flex align-items-center">
        {actions.map((action, index) => (
          <button
            key={action.key}
            type="button"
            class={`btn btn-sm ${action.buttonClass || 'btn-outline-secondary'}${index < actions.length - 1 || hasMenu ? ' me-1' : ''}`}
            title={action.label}
            data-action={action.onClick ? undefined : action.dataAction}
            {...(action.onClick ? {} : (action.dataParams || {}))}
            onClick={(e) => {
              e.stopPropagation()
              if (action.onClick) action.onClick(e)
            }}
          >
            <i class={`bi ${action.icon}`} aria-hidden="true"></i>
          </button>
        ))}
        {hasMenu && (
          <div class="dropdown">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary drive-row-actions__menu-btn"
              data-bs-toggle="dropdown"
              data-bs-strategy="fixed"
              aria-expanded="false"
              aria-label={t('drive.more_actions')}
              onClick={(e) => e.stopPropagation()}
            >
              <i class="bi bi-three-dots-vertical" aria-hidden="true"></i>
            </button>
            <ul class="dropdown-menu dropdown-menu-end shadow-sm">
              {menuActions.map((action) => (
                <li key={action.key}>
                  <MenuItem action={action} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Mobile: single existing ⋮ — menuActions merged at top */}
      <div class="dropdown d-md-none">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary drive-row-actions__menu-btn"
          data-bs-toggle="dropdown"
          data-bs-strategy="fixed"
          aria-expanded="false"
          aria-label={t('drive.more_actions')}
          onClick={(e) => e.stopPropagation()}
        >
          <i class="bi bi-three-dots-vertical" aria-hidden="true"></i>
        </button>
        <ul class="dropdown-menu dropdown-menu-end shadow-sm">
          {mobileItems.map((action) => (
            action.divider ? (
              <li key={action.key}><hr class="dropdown-divider" /></li>
            ) : (
              <li key={action.key}>
                <MenuItem action={action} />
              </li>
            )
          ))}
        </ul>
      </div>
    </td>
  )
}
